import { useState, useEffect, useRef } from "react";
import apiClient from "@/lib/api";
import { useAuth } from "@/App";
import { useCompanyFilter } from "@/contexts/CompanyFilterContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Send, Bot, User, Sparkles, MessageSquare, Trash2, Loader2, AlertCircle } from "lucide-react";

const Chat = () => {
  const { user } = useAuth();
  const { selectedCompany, getSelectedCompanyName, getApiCompanyId } = useCompanyFilter();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [lastCompany, setLastCompany] = useState(selectedCompany);
  const scrollRef = useRef(null);

  // Clear chat when company changes
  useEffect(() => {
    if (selectedCompany !== lastCompany && messages.length > 0) {
      setMessages([]);
      setSessionId(null);
    }
    setLastCompany(selectedCompany);
  }, [selectedCompany, lastCompany, messages.length]);

  useEffect(() => {
    // Auto-scroll to bottom when messages change
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setLoading(true);

    // Add user message to chat
    const messageId = `msg-${Date.now()}`;
    setMessages(prev => [...prev, { id: messageId, role: "user", content: userMessage }]);

    try {
      const payload = { 
        message: userMessage, 
        session_id: sessionId,
        company_id: getApiCompanyId()
      };
      
      const response = await apiClient.post(`/api/chat`, payload);

      // Set session ID for continued conversation
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
      }

      // Add assistant response
      const responseId = `msg-${Date.now()}`;
      setMessages(prev => [...prev, { id: responseId, role: "assistant", content: response.data.response }]);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to send message";
      toast.error(errorMessage);
      // Remove the user message if there was an error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
  };

  const getCompanyLabel = () => {
    if (selectedCompany === "all") return "all your accessible companies";
    return getSelectedCompanyName();
  };

  const suggestedQuestions = selectedCompany === "all" 
    ? [
        "How many pay rules are non-compliant?",
        "Which company has the most compliance issues?",
        "Show me a summary of all companies",
        "What actions should I take to improve compliance?"
      ]
    : [
        `How many pay rules does this company have?`,
        `What compliance issues exist?`,
        `Which states have issues?`,
        `What's the estimated financial risk?`
      ];

  return (
    <div className="p-6 h-[calc(100vh-4rem)] flex flex-col" data-testid="chat-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            AI Assistant
          </h1>
          <p className="text-muted-foreground mt-1">
            Ask questions about your pay rule data
          </p>
        </div>
        <div className="flex items-center gap-3">
          {messages.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearChat}
              className="gap-2"
              data-testid="clear-chat-btn"
            >
              <Trash2 className="w-4 h-4" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Company Context Banner */}
      {selectedCompany !== "all" && (
        <div className="mb-4 p-3 bg-primary/10 border border-primary/20 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-primary" />
          <span className="text-sm">
            Chat context is filtered to <strong>{getSelectedCompanyName()}</strong>. 
            The AI will only discuss this company's data.
          </span>
        </div>
      )}

      {/* Chat Container */}
      <Card className="flex-1 flex flex-col overflow-hidden">
        <ScrollArea className="flex-1 p-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8">
              <div className="w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center mb-4">
                <MessageSquare className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-xl font-semibold font-['Manrope'] mb-2">
                Start a Conversation
              </h2>
              <p className="text-muted-foreground mb-6 max-w-md">
                Ask me anything about {getCompanyLabel()}'s pay rules, compliance status, or wage law requirements.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-2xl">
                {suggestedQuestions.map((question) => (
                  <Button
                    key={question}
                    variant="outline"
                    className="justify-start text-left h-auto py-3 px-4"
                    onClick={() => {
                      setInput(question);
                    }}
                    data-testid={`suggested-question-${question.slice(0, 20).replace(/\s/g, '-')}`}
                  >
                    <span className="text-sm">{question}</span>
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex items-start gap-3 ${
                    message.role === "user" ? "flex-row-reverse" : ""
                  }`}
                  data-testid={`chat-message-${message.id}`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      message.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-secondary-foreground"
                    }`}
                  >
                    {message.role === "user" ? (
                      <User className="w-4 h-4" />
                    ) : (
                      <Bot className="w-4 h-4" />
                    )}
                  </div>
                  <div
                    className={`chat-message ${
                      message.role === "user"
                        ? "chat-message-user"
                        : "chat-message-assistant"
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center">
                    <Bot className="w-4 h-4" />
                  </div>
                  <div className="chat-message chat-message-assistant">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm text-muted-foreground">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={scrollRef} />
            </div>
          )}
        </ScrollArea>

        {/* Input Area */}
        <div className="border-t p-4">
          <form onSubmit={sendMessage} className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={`Ask about ${selectedCompany === "all" ? "your pay rules" : getSelectedCompanyName()}...`}
              className="flex-1"
              disabled={loading}
              data-testid="chat-input"
            />
            <Button type="submit" disabled={loading || !input.trim()} data-testid="send-btn">
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            {selectedCompany === "all" 
              ? "AI responses based on all your accessible companies."
              : `AI responses filtered to ${getSelectedCompanyName()} only.`
            }
          </p>
        </div>
      </Card>
    </div>
  );
};

export default Chat;
